import { FileSpreadsheet, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/index.js";
import { auditClock, auditDate } from "../components/payroll/AuditTimeline.js";
import type {
  AdminUser,
  PohodaExportVersionSummary,
} from "../types/payrollAdmin.js";
import type { OpenProjectPohodaPreview } from "../types/openproject.js";
export interface PohodaVersionsModel {
  exports: PohodaExportVersionSummary[];
  preview: { blockedCount: number } | null | undefined;
  dataQuality: OpenProjectPohodaPreview["dataQuality"] | undefined;
  missingCodeUsers: AdminUser[];
  outdated: boolean;
  requirementsComplete: boolean;
  canGenerate: boolean;
  loading: boolean;
  generating: boolean;
  downloading: boolean;
}
export function PayrollPohodaVersions({
  model,
  onGenerate,
  onOpen,
  onDownload,
}: {
  model: PohodaVersionsModel;
  onGenerate: () => void;
  onOpen: (id: number) => void;
  onDownload: (id: number) => void;
}) {
  const {
    exports: pohodaExports,
    preview: pohodaPreview,
    dataQuality: pohodaDataQuality,
    missingCodeUsers: missingPayrollCodeUsers,
    outdated,
    requirementsComplete,
    canGenerate: canGeneratePohoda,
    loading,
    generating,
    downloading,
  } = model;
  return (
    <div className="payroll-pohoda-content mt-3 min-w-0 rounded-xl border border-border bg-background">
      {outdated && (
        <p
          role="status"
          className="border-b border-border px-3 py-2 text-sm text-warning"
        >
          Source details changed. Saved POHODA input remains available; generate
          a new version when needed.
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
        <p className="text-xs font-semibold text-foreground">Versions</p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-foreground-subtle">
            {pohodaExports.length}
          </span>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            loading={generating}
            onClick={() => onGenerate()}
            disabled={
              !canGeneratePohoda ||
              (!pohodaPreview && pohodaExports.length === 0) ||
              (pohodaPreview?.blockedCount ?? 0) > 0
            }
          >
            {pohodaExports.length > 0 ? (
              <RotateCcw size={14} aria-hidden="true" />
            ) : (
              <FileSpreadsheet size={14} aria-hidden="true" />
            )}
            {pohodaExports.length > 0
              ? "Generate new version"
              : "Generate input"}
          </Button>
        </div>
      </div>
      {(pohodaDataQuality?.disposition === "review_required" ||
        missingPayrollCodeUsers.length > 0 ||
        (pohodaExports.length > 0 && !requirementsComplete)) && (
        <details className="border-b border-border bg-background px-3 text-sm text-foreground">
          <summary className="flex min-h-10 cursor-pointer flex-wrap items-center gap-x-1.5 py-2 text-xs font-semibold marker:text-foreground-muted">
            <span>Export checks</span>
            {missingPayrollCodeUsers.length > 0 && (
              <span className="text-warning">
                · {missingPayrollCodeUsers.length} employee code
                {missingPayrollCodeUsers.length === 1 ? "" : "s"}
              </span>
            )}
            {(pohodaDataQuality?.missingSubjectEntryCount ?? 0) > 0 && (
              <span className="text-warning">
                · {pohodaDataQuality?.missingSubjectEntryCount} source notes
              </span>
            )}
            {pohodaExports.length > 0 && !requirementsComplete && (
              <span className="text-warning">· input changed</span>
            )}
          </summary>
          <div className="space-y-3 border-t border-border py-3 text-xs leading-5 text-foreground-muted">
            {missingPayrollCodeUsers.length > 0 && (
              <section>
                <p className="font-semibold text-foreground">
                  Employee codes need review before export
                </p>
                <p className="mt-0.5">
                  {missingPayrollCodeUsers.length} included Work Profile
                  {missingPayrollCodeUsers.length === 1 ? " has" : "s have"} no
                  employee code:{" "}
                  {missingPayrollCodeUsers
                    .map((user) => user.displayName || user.username)
                    .join(", ")}
                  . The Pohoda export will still include
                  {missingPayrollCodeUsers.length === 1
                    ? " this profile"
                    : " these profiles"}
                  .
                </p>
              </section>
            )}
            {pohodaDataQuality?.disposition === "review_required" && (
              <section>
                <p className="font-semibold text-foreground">Source notes</p>
                {pohodaDataQuality.missingSubjectEntryCount > 0 && (
                  <p className="mt-0.5">
                    {pohodaDataQuality.missingSubjectEntryCount} entries (
                    {pohodaDataQuality.missingSubjectHours.toFixed(2)} h) have
                    no subject.
                  </p>
                )}
                {pohodaDataQuality.unclassifiedSubjectEntryCount > 0 && (
                  <p>
                    {pohodaDataQuality.unclassifiedSubjectEntryCount} entries (
                    {pohodaDataQuality.unclassifiedSubjectHours.toFixed(2)} h)
                    use other task subjects. These may include ordinary project
                    work.
                  </p>
                )}
                <p>
                  Check the source, sync, then unlock and re-lock affected
                  timesheets in Prepare records. Saved exports stay unchanged.
                </p>
              </section>
            )}
            {pohodaExports.length > 0 && !requirementsComplete && (
              <section>
                <p className="font-semibold text-foreground">
                  Existing Pohoda input is stale
                </p>
                <p className="mt-0.5">
                  The selected-month employee set or another payroll input
                  changed. Preserve the earlier version and generate a new one
                  before continuing.
                </p>
              </section>
            )}
          </div>
        </details>
      )}
      {pohodaExports.length > 0 && (
        <p className="border-b border-border px-3 py-1.5 text-[11px] text-foreground-subtle">
          Saved files are unchanged by current source checks.
        </p>
      )}
      {loading ? (
        <p className="px-3 py-3 text-xs text-foreground-muted">
          Loading generated files…
        </p>
      ) : pohodaExports.length ? (
        <ul
          className="divide-y divide-border"
          aria-label="Saved Pohoda versions"
        >
          {pohodaExports.map((exportRun) => (
            <li
              key={exportRun.id}
              className="flex flex-wrap items-center justify-between gap-3 px-3 py-0.5"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-md border border-border bg-surface px-1.5 py-0.5 text-xs font-semibold text-foreground">
                    v{exportRun.versionNumber}
                  </span>
                  <p className="truncate text-xs font-medium text-foreground">
                    {exportRun.fileName}
                  </p>
                </div>
                <p className="mt-1 text-[11px] text-foreground-subtle">
                  {exportRun.generatedByName} ·{" "}
                  {auditDate(exportRun.generatedAt)}{" "}
                  {auditClock(exportRun.generatedAt)} ·{" "}
                  {exportRun.includedEmployeeCount} employee
                  {exportRun.includedEmployeeCount === 1 ? "" : "s"}
                  {exportRun.warningCount > 0 &&
                    ` · ${exportRun.warningCount} recorded warning${exportRun.warningCount === 1 ? "" : "s"}`}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() => onOpen(exportRun.id)}
                  className="pohoda-file-action cursor-pointer rounded-md px-2 text-xs font-medium text-foreground-muted hover:bg-elevated hover:text-foreground"
                >
                  Open
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() => onDownload(exportRun.id)}
                  disabled={downloading}
                  className="pohoda-file-action cursor-pointer rounded-md border border-border px-2.5 text-xs font-medium text-foreground hover:bg-elevated disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Download
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-3 py-3 text-xs text-foreground-muted">
          No Pohoda input has been generated for this month.
        </p>
      )}
    </div>
  );
}
